import {
    type AsyncStateUpdate,
    Button,
    FlexColumn,
    isOk,
    Modal,
    NumberInput,
    renderCurrency,
    roundFloat,
    useToasts,
    useStateFromProps,
} from '@synqo/frontend-component-library';
import { type ApiInvoice, type ApiOrder, EnumInvoiceType } from '@Model';
import { FC, useCallback, useMemo } from 'react';
import { useCashPayment } from '@Hooks/useCashPayment';

interface BaseCashPaymentProperties<T extends ApiOrder | ApiInvoice> {
    entity: T;
    currency: string;
    totalGrossPrice: number;
    isOpen: boolean;
    close: () => void;
    onBeforeStartPayment?: () => Promise<boolean>;
    onSuccessfulPayment?: (invoices: ApiInvoice[]) => void;
}

interface OrderCashPaymentProperties extends BaseCashPaymentProperties<ApiOrder> {
    type: 'order';
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
}

interface InvoiceCashPaymentProperties extends BaseCashPaymentProperties<ApiInvoice> {
    type: 'invoice';
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;
}

type CashPaymentProperties = OrderCashPaymentProperties | InvoiceCashPaymentProperties;

export const CashPaymentModal: FC<CashPaymentProperties> = ({
    type,
    entity,
    totalGrossPrice,
    currency,
    isOpen,
    close,
    onSuccessfulPayment,
    onBeforeStartPayment,
    requestUpdateState,
}) => {
    const { startPayment } = useCashPayment(type);
    const { pushToast, pushAllToasts, remove } = useToasts();

    const absolutePrice = Math.abs(roundFloat(totalGrossPrice, 2));
    const absolutePriceRendered = renderCurrency(absolutePrice, 2);

    const [paidAmount, setPaidAmount] = useStateFromProps(absolutePrice);

    const change = roundFloat(paidAmount - absolutePrice, 2);
    const changeFormatted = renderCurrency(change, 2);

    const performPayment = useCallback(
        async (id: string) => {
            const result = await startPayment(id);

            if (isOk(result)) {
                pushToast({
                    title: 'Verbuchung',
                    message: 'Zahlung erfolgreich verbucht.',
                    variant: 'success',
                });
                if (onSuccessfulPayment !== undefined) {
                    onSuccessfulPayment(result.data);
                }
            } else {
                pushToast({
                    title: 'Verbuchung',
                    message: 'Verbuchung fehlgeschlagen!',
                    variant: 'error',
                });
                pushAllToasts(result.messages);
            }
        },
        [startPayment, pushToast, onSuccessfulPayment, pushAllToasts],
    );

    const confirm = useCallback(async () => {
        close();

        if (onBeforeStartPayment) {
            const result = await onBeforeStartPayment();
            if (!result) {
                return;
            }
        }

        const pendingToastId = pushToast({
            title: 'Verbuchung',
            message: 'Verbuchung läuft...',
            variant: 'success',
            timeout: undefined,
            loading: true,
        });

        requestUpdateState(async (latest) => {
            await performPayment(latest.id);
            remove(pendingToastId);
            return undefined;
        });
    }, [onBeforeStartPayment, close, pushToast, requestUpdateState, performPayment, remove]);

    const updatePaidAmount = useCallback(
        (amount: number) => {
            setPaidAmount(amount);
            return true;
        },
        [setPaidAmount],
    );

    const isCredit = useMemo(() => {
        if (type === 'invoice') {
            return entity.type === EnumInvoiceType.CreditNote;
        }
        return totalGrossPrice < 0;
    }, [totalGrossPrice, entity.type, type]);

    const modalTitle = isCredit ? 'Jetzt auszahlen' : 'Jetzt kassieren';

    return (
        <Modal title={modalTitle} onClose={close} isOpen={isOpen}>
            <FlexColumn align="start">
                {isCredit ? (
                    <>Auszuzahlender Betrag: {absolutePriceRendered}</>
                ) : (
                    <>Zu zahlender Betrag: {absolutePriceRendered}</>
                )}
                <NumberInput
                    name="paid_amount"
                    label={`Gezahlter Betrag (${currency})`}
                    value={absolutePrice}
                    valid={change >= 0}
                    onChange={updatePaidAmount}
                    decimals={2}
                />
                {change > 0 && <>Wechselgeld: {changeFormatted}</>}
                <Button
                    appearance="filled"
                    fullWidth
                    onClick={confirm}
                    locked={change < 0}
                    tooltip={change < 0 ? 'Der gezahlte Betrag ist zu niedrig.' : ''}
                >
                    Bestätigen
                </Button>
            </FlexColumn>
        </Modal>
    );
};
